import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Cloud, CloudFog, CloudLightning, CloudRain, CloudSun, Droplets, Maximize2, Minimize2, Sun, Wind } from 'lucide-react';
import { DarshanAlertBanner } from './DarshanAlertBanner';

interface TopBarProps {
  hideBanner?: boolean;
}

interface CurrentWeather {
  temperature_2m: number;
  apparent_temperature: number;
  relative_humidity_2m: number;
  weather_code: number;
  wind_speed_10m: number;
}

const weatherDescription = (code: number): string => {
  if (code === 0) return 'Clear';
  if (code <= 2) return 'Partly cloudy';
  if (code === 3) return 'Cloudy';
  if (code === 45 || code === 48) return 'Fog';
  if (code <= 57) return 'Drizzle';
  if (code <= 67 || (code >= 80 && code <= 82)) return 'Rain';
  if (code <= 77) return 'Snow';
  if (code >= 95) return 'Thunderstorm';
  return 'Showers';
};

const WeatherIcon: React.FC<{ code: number; className?: string }> = ({ code, className }) => {
  if (code === 0) return <Sun className={className} />;
  if (code <= 2) return <CloudSun className={className} />;
  if (code === 45 || code === 48) return <CloudFog className={className} />;
  if (code <= 57) return <CloudRain className={className} />;
  if (code <= 67 || (code >= 80 && code <= 82)) return <CloudRain className={className} />;
  if (code >= 95) return <CloudLightning className={className} />;
  return <Cloud className={className} />;
};

const WeatherDetails: React.FC = () => {
  const [weather, setWeather] = useState<CurrentWeather | null>(null);

  useEffect(() => {
    let active = true;
    const loadWeather = async () => {
      try {
        const params = new URLSearchParams({
          latitude: '16.5062',
          longitude: '80.6480',
          current: 'temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,wind_speed_10m',
          timezone: 'Asia/Kolkata',
        });
        const response = await fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`);
        if (!response.ok) throw new Error(`Weather request failed: ${response.status}`);
        const data = await response.json();
        if (active && data.current) setWeather(data.current as CurrentWeather);
      } catch (error) {
        console.warn('Could not load Vijayawada weather:', error);
      }
    };

    void loadWeather();
    const interval = window.setInterval(loadWeather, 15 * 60 * 1000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, []);

  if (!weather) return null;
  const weatherClass = 'h-4 w-4 shrink-0 text-orange-500 dark:text-orange-300';
  return (
    <div
      className="hidden md:flex items-center gap-2.5 rounded-lg border border-orange-100 bg-orange-50/80 px-2.5 py-1.5 text-xs text-neutral-700 dark:border-orange-900/50 dark:bg-orange-950/30 dark:text-neutral-200"
      title={`Vijayawada weather: ${weatherDescription(weather.weather_code)}, feels like ${Math.round(weather.apparent_temperature)}°C, humidity ${weather.relative_humidity_2m}%, wind ${Math.round(weather.wind_speed_10m)} km/h`}
      aria-label={`Vijayawada weather: ${Math.round(weather.temperature_2m)} degrees Celsius, ${weatherDescription(weather.weather_code)}`}
    >
      <WeatherIcon code={weather.weather_code} className={weatherClass} />
      <span className="font-bold tabular-nums">{Math.round(weather.temperature_2m)}°C</span>
      <span className="whitespace-nowrap">{weatherDescription(weather.weather_code)}</span>
      <span className="hidden xl:flex items-center gap-1 border-l border-orange-200 pl-2 dark:border-orange-900/70">
        <span className="flex items-center gap-0.5"><CloudSun className="h-3.5 w-3.5" />{Math.round(weather.apparent_temperature)}°</span>
        <span className="flex items-center gap-0.5"><Droplets className="h-3.5 w-3.5" />{weather.relative_humidity_2m}%</span>
        <span className="flex items-center gap-0.5"><Wind className="h-3.5 w-3.5" />{Math.round(weather.wind_speed_10m)}</span>
      </span>
      <span className="text-neutral-500 dark:text-neutral-400">Vijayawada</span>
    </div>
  );
};

export const TopBar: React.FC<TopBarProps> = ({ hideBanner = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicDarshan = location.pathname.includes('/darshan-timings/public');
  const showBanner = !hideBanner && !isPublicDarshan;
  const [isFullscreen, setIsFullscreen] = useState<boolean>(() => !!document.fullscreenElement);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        } else if ((document.documentElement as any).webkitRequestFullscreen) {
          await (document.documentElement as any).webkitRequestFullscreen();
        } else if ((document.documentElement as any).msRequestFullscreen) {
          await (document.documentElement as any).msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (err) {
      console.warn('Error attempting to toggle fullscreen:', err);
    }
  };

  return (
    <div className="sticky top-0 z-40 w-full flex flex-col">
      {showBanner && <DarshanAlertBanner />}
      <header className="w-full h-14 border-b border-neutral-200/80 dark:border-[#262b37] bg-white dark:bg-[#14161d]/85 backdrop-blur-md px-4 flex items-center justify-between transition-colors duration-200">
        {/* Left: Brand & Status */}
        <div className="flex items-center gap-3">
          <div
            onClick={() => navigate('/')}
            className="flex items-center gap-2 cursor-pointer group"
          >
            <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center shadow-md shadow-orange-500/20 group-hover:scale-105 transition-transform">
              <img
                src="/logo.png"
                alt="Dasara Logo"
                className="w-[48px] h-[48px] object-contain drop-shadow-md"
                style={{ width: '48px', height: '48px', minWidth: '48px', minHeight: '48px' }}
              />
            </div>
            <div className="flex flex-col">
              <span className="font-extrabold text-sm sm:text-base tracking-tight text-neutral-900 dark:text-neutral-100">
                Integrated Command – Dasara Navaratri 2026
              </span>
            </div>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-3 sm:gap-4">
          <WeatherDetails />
          {/* Designed by Runners Software Technologies in text-lg */}
          <div className="text-base sm:text-[13px] font-medium text-neutral-600 dark:text-neutral-300">
            Design & Developed by <strong className="font-bold text-neutral-900 dark:text-white">Runners Software Technology</strong>
          </div>

          {/* Global Full Screen Toggle */}
          <button
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit Full Screen' : 'Enter Full Screen'}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 dark:border-[#262c3b] bg-neutral-100/80 dark:bg-[#181b23] text-neutral-700 dark:text-neutral-300 hover:text-orange-500 dark:hover:text-orange-400 hover:border-orange-500/50 hover:bg-orange-500/10 dark:hover:bg-orange-500/10 transition-all duration-200 cursor-pointer text-xs font-semibold shadow-xs"
          >
            {isFullscreen ? (
              <>
                <Minimize2 className="w-3.5 h-3.5 text-orange-500" />
                {!hideBanner && <span>Exit Full Screen</span>}
              </>
            ) : (
              <>
                <Maximize2 className="w-3.5 h-3.5" />
                {!hideBanner && <span>Full Screen</span>}
              </>
            )}
          </button>
        </div>
      </header>
    </div>
  );
};
